import { groupBy } from "lodash-es";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { SupabaseCustomEmail } from "../../channels/email/server/supabase-custom-email-service";
import { SupabaseCustomEmailService } from "../../channels/email/server/supabase-custom-email-service";
import { SupabaseAccountLimitService } from "../../generic/server/supabase-account-service";
import type { Program } from "../../programs/server/supabase-program-service";
import { SupabaseProgramService } from "../../programs/server/supabase-program-service";
import type { SupabaseSegmentWithFavourite } from "../../segment/server/supabase-segment-service";
import { SupabaseSegmentService } from "../../segment/server/supabase-segment-service";
import type { Permissions, PersonDetails } from "../server/supabase-person-service";
import {
  SupabasePermissionsService,
  SupabasePersonDetailsService,
} from "../server/supabase-person-service";

type PermissionsTableData = {
  programs: Program[];
  permissions: Permissions[];
  employees: PersonDetails[];
  limits: { contributors: number; super_admins: number };
  programMap: { [k: string]: Program };
  superAdmins: Permissions[] | null;
  contributors: Permissions[] | null;
  reviewers: Permissions[] | null;
  capacity: { contributors: number; super_admins: number };
  availableEmployees: PersonDetails[];
  segments: SupabaseSegmentWithFavourite[];
  custom_emails: SupabaseCustomEmail[];
};

export function usePermissionsTableData(): ReturnType<
  typeof useSupabaseLoader<PermissionsTableData>
> {
  return useSupabaseLoader(async ({ supabase, account_id }) => {
    const [
      { data: programData, error: programError },
      { data: permsData, error: permsError },
      { data: personTextData, error: personTextError },
      { data: limitData, error: limitError },
      { data: segmentData, error: segmentError },
      { data: emailData, error: emailError },
    ] = await Promise.all([
      new SupabaseProgramService(supabase).getAll(null, { order: [{ column: "id" }] }),
      new SupabasePermissionsService(supabase).getAll(account_id, { order: [{ column: "id" }] }),
      new SupabasePersonDetailsService(supabase).getAll(account_id, {
        order: [{ column: "id" }],
      }),
      new SupabaseAccountLimitService(supabase).getKeys(["contributors", "super_admins"]),
      new SupabaseSegmentService(supabase).getAllWithFavoriteSegment(account_id, {
        order: [{ column: "id" }],
      }),
      new SupabaseCustomEmailService(supabase).getAll(account_id, { order: [{ column: "id" }] }),
    ]);

    if (programError || permsError || personTextError || limitError || segmentError || emailError)
      return {
        programs: [] as Program[],
        permissions: [] as Permissions[],
        employees: [] as PersonDetails[],
        limits: { contributors: 0, super_admins: 0 },
        programMap: {},
        superAdmins: null,
        contributors: null,
        reviewers: null,
        capacity: { contributors: 0, super_admins: 0 },
        availableEmployees: [],
        segments: [],
        custom_emails: [],
      };

    const programs = programData ?? [];
    const permissions = permsData ?? [];
    const { superAdmins, contributors, reviewers } = groupBy(permissions, (p) =>
      p.super_admin ? "superAdmins" : p.reviewer ? "reviewers" : "contributors",
    );
    const limits = {
      contributors: limitData?.find((d) => d.key === "contributors")?.count ?? 0,
      super_admins: limitData?.find((d) => d.key === "super_admins")?.count ?? 0,
    };
    const employees = personTextData ?? [];

    return {
      programs,
      permissions,
      employees,
      limits,
      programMap: Object.fromEntries(programs.map((p) => [p.id, p]) ?? []),
      superAdmins: superAdmins!,
      contributors: contributors!,
      reviewers: reviewers!,
      capacity: {
        contributors: Math.max(0, limits.contributors - (contributors?.length ?? 0)),
        super_admins: Math.max(0, limits.super_admins - (superAdmins?.length ?? 0)),
      },
      availableEmployees: employees.filter(({ id }) => !permissions.find((p) => p.id === id)),
      segments: segmentData ?? [],
      custom_emails: emailData ?? [],
    };
  }, []);
}
